import { useEffect, useState } from "react";
import { View } from "react-native";
import Animated from "react-native-reanimated";
import FloatingBackgroundImages from "src/components/FloatingBackgroundImages";
import Loading from "src/components/Loading";
import OpacityView from "src/components/OpacityView";
import Touchable from "src/components/Touchable";
import { parseGatheringThumbnailUrl } from "src/constants/GatheringThumbnail";
import { useGatheringStore } from "src/contexts/GatheringContext";
import parseGatheringPreviewTemplateUrl from "src/helpers/parseV0TemplateUrl/parseGatheringPreviewTemplateUrl";
import useAnimatedOpacityStyle from "src/hooks/useAnimatedOpacityStyle";
import useDevice from "src/hooks/useDevice";
import useDim from "src/hooks/useDim";
import useGatheringPhotosWithLocal from "src/hooks/useGatheringPhotosWithLocal";
import useNoScroll from "src/hooks/useNoScroll";
import useStyle from "src/hooks/useStyle";
import { color } from "src/styles";
import { FixedPosition } from "src/styles/position";
import { radiusMedium } from "src/styles/radius";
import { paddingLarge, paddingMedium, paddingXXL } from "src/styles/spacing";
import { Image, Text, useSafeArea } from "src/swsh-native";

const FilterLoadingContent = ({ forceDismiss }: { forceDismiss: () => void }) => {
	const gatheringId = useGatheringStore((state) => state.gatheringId);
	const { photos } = useGatheringPhotosWithLocal(gatheringId, {
		baseLimit: 32,
		key: "floatingFilterLoading",
	});
	const { theme } = useStyle();
	const { device } = useDevice();
	const name = useGatheringStore((state) => state.name);
	const thumbnail = useGatheringStore((state) => state.thumbnail);
	const safeArea = useSafeArea();
	const thumbnailUrl = parseGatheringThumbnailUrl({
		readUrl: thumbnail?.readUrl,
	});

	return (
		<>
			<FloatingBackgroundImages
				photos={photos.map((p) => ({
					src: parseGatheringPreviewTemplateUrl({ readUrl: p.readUrl }),
				}))}
			/>
			<Text
				lg
				semibold
				style={{
					textAlign: "center",
				}}
			>
				{name}
			</Text>
			<OpacityView
				exiting={false}
				delay={800}
				duration={800}
				style={{
					marginTop: paddingMedium,
					flexDirection: "row",
					alignItems: "center",
				}}
			>
				{/** For spacing purposes */}
				<Loading style={{ opacity: 0 }} />
				<Text
					color="TextLight"
					style={{
						marginHorizontal: paddingMedium,
					}}
				>
					swsh is finding photos of{" "}
					<Text bold color="TextLight">
						you
					</Text>
				</Text>
				<OpacityView delay={3000}>
					<Loading color={color.TextLight[theme]} />
				</OpacityView>
			</OpacityView>
			<View
				style={{
					width: 210,
					aspectRatio: 3 / 4,
					marginTop: paddingLarge,
				}}
			>
				{!!thumbnailUrl && (
					<Image
						alt="Album thumbnail"
						src={thumbnailUrl}
						dim="auto"
						style={{
							borderRadius: radiusMedium,
							width: "100%",
							height: "100%",
						}}
					/>
				)}
			</View>

			<Touchable
				onPress={forceDismiss}
				containerStyle={
					device === "mobile"
						? {
								position: "absolute",
								bottom: safeArea.bottom + paddingXXL,
							}
						: {
								marginTop: paddingXXL,
							}
				}
			>
				<Text color="TextLight">Skip</Text>
			</Touchable>
		</>
	);
};

const FilterLoading = ({
	forceDismiss,
	enabled,
}: {
	forceDismiss: () => void;
	enabled: boolean;
}) => {
	const dim = useDim();
	const { theme } = useStyle();
	const animatedStyle = useAnimatedOpacityStyle(enabled ? 1 : 0, {
		duration: 400,
	});
	const [isRendered, setIsRendered] = useState(enabled);
	useNoScroll(enabled);

	useEffect(() => {
		if (enabled) {
			setIsRendered(true);
		} else {
			setTimeout(() => {
				setIsRendered(false);
			}, 400);
		}
	}, [enabled]);

	if (!isRendered) return null;
	return (
		<Animated.View
			style={[
				{
					position: FixedPosition,
					zIndex: 1,
					width: dim.width,
					height: dim.height,
					overflow: "hidden",
					alignItems: "center",
					justifyContent: "center",
					paddingHorizontal: paddingXXL,
					backgroundColor: color.Background[theme],
				},
				animatedStyle,
			]}
			pointerEvents={enabled ? "box-none" : "none"}
			testID="FilterLoading"
		>
			<FilterLoadingContent forceDismiss={forceDismiss} />
		</Animated.View>
	);
};
export default FilterLoading;
